import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PaymentMethod } from '../types/PaymentMethod';
import {
  getPaymentMethods,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
  definirEscopoDoMetodo, definirCartaoDoMetodo, definirPontosDoMetodo, definirContaDoMetodo, pedirProgramaDaConta,
  marcarHistoricoDoMetodo,
  getPaymentMethodsSpending,
  parseQuickMethod,
  marcarFaturaPaga,
  desmarcarFaturaPaga,
} from '../services/api';
import { rotuloDoMes } from './SeletorDeMes';
import { nomeDoCartao } from '../utils/nomeDoCartao';
import VantagensDoCartao from './VantagensDoCartao';
import VantagensDaConta from './VantagensDaConta';
import { PROGRAMAS_DA_CONTA, nomeDaConta, programaDaConta, semNiveis } from '../data/vantagensDaConta';
import { BANDEIRAS_E_NIVEIS } from '../data/vantagensDoCartao';
import { SeusPontos } from './SeusPontos';
import './PaymentMethods.css';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import Toast from './Toast';
/* O MESMO número que o schema do servidor usa — ver utils/limites.ts. */
import { LIMITES } from '../utils/limites';
import { ehTetoDeIa, AVISO_TETO_DE_IA } from '../utils/tetoDeIa';
import { formatPct, brlOrMasked } from '../utils/format';
import { useTelaEstreita } from '../utils/telaEstreita';
import './QuickEntry.css';
import { useConfirmar } from '../contexts/Confirmacao';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';

interface PaymentMethodsProps {
  /** O olhinho de "Ocultar valores" do cabeçalho. Ver `utils/format.ts`. */
  hideValues?: boolean;

  refreshTrigger?: number;
  onMethodsChange?: () => void;
  selectedMonth?: { key: string; label: string } | null;
  /**
   * Os métodos que o app já tem em memória. Com eles a seção abre desenhada, e
   * a leitura nova acontece por trás, sem trocar a lista por "Carregando".
   */
  metodosIniciais?: PaymentMethod[];
  /** O toque no card filtra a lista de Transações por este método. */
  onVerTransacoes?: (paymentMethodId: string) => void;
}

/*
 * O gasto por método da última leitura, para a seção não abrir com os valores
 * vazios. A chave leva os ids dos métodos e o mês: outra conta, ou outro mês,
 * não casa, e aí os valores simplesmente esperam a leitura.
 */
let gastoGuardado: { chave: string; gasto: Record<string, number> } | null = null;
const chaveDoGasto = (metodos: PaymentMethod[], mes?: string): string =>
  `${metodos.map(m => m.id).sort().join(',')}|${mes ?? ''}`;

interface FormState {
  name: string;
  monthlyLimit: string;
  billingDay: string;
  closingDay: string;
  /** A bandeira e o nível do cartão, como códigos (`MASTERCARD`, `BLACK`). Vazio = não é cartão, ou não sei. */
  bandeira: string;
  nivel: string;
  /** O programa da conta do banco e o nível dela nele. Vazio = não informado. */
  contaPrograma: string;
  contaNivel: string;
  daCasa: boolean;
  /** Quanto o cartão pontua, como ela digita ("2,2"). Vazio = não informado. */
  pontos: string;
  pontosMoeda: 'BRL' | 'USD';
  pontosAlta: string;
  pontosFatura: string;
}

/**
 * O que o campo de pontuação mostra para um método: a que ela informou, senão
 * a que o banco publica para o cartão (13/10: o campo abria vazio no Santander
 * Unique e no Elite, embora o app já usasse a publicada nas contas).
 */
const pontosNoFormulario = (m: PaymentMethod): { pontos: string; pontosMoeda: 'BRL' | 'USD'; pontosAlta: string; pontosFatura: string } => {
  const v = m.pontosTaxa
    ? { taxa: m.pontosTaxa, moeda: m.pontosMoeda ?? 'USD', taxaAlta: m.pontosTaxaAlta ?? null, faturaMinima: m.pontosFaturaMinima ?? null }
    : m.pontuacaoPublicada ?? null;
  if (!v) return SEM_PONTOS;
  return {
    pontos: String(v.taxa).replace('.', ','),
    pontosMoeda: v.moeda,
    pontosAlta: v.taxaAlta ? String(v.taxaAlta).replace('.', ',') : '',
    pontosFatura: v.faturaMinima ? String(v.faturaMinima) : '',
  };
};

/** O valor do seletor para "Meu banco não está na lista". Nunca é gravado: abre o pedido. */
const FORA_DA_LISTA = '__fora-da-lista__';

const SEM_PONTOS = { pontos: '', pontosMoeda: 'USD' as const, pontosAlta: '', pontosFatura: '' };

const EMPTY_FORM: FormState = {
  name: '', monthlyLimit: '', billingDay: '', closingDay: '', daCasa: false, bandeira: '', nivel: '',
  contaPrograma: '', contaNivel: '', ...SEM_PONTOS,
};

/** "2,2" ou "2.2" → 2.2; "4.000" → 4000; vazio ou inválido → null. */
const numeroDoCampo = (v: string): number | null => {
  const t = v.trim();
  // Ponto só é milhar em "4.000": "2.2" é dois vírgula dois, e não vinte e dois.
  const valor = /^\d{1,3}(\.\d{3})+(,\d+)?$/.test(t)
    ? parseFloat(t.replace(/\./g, '').replace(',', '.'))
    : parseFloat(t.replace(',', '.'));
  return Number.isFinite(valor) && valor > 0 ? valor : null;
};

const PaymentMethods: React.FC<PaymentMethodsProps> = ({
  refreshTrigger, onMethodsChange, selectedMonth, hideValues, metodosIniciais, onVerTransacoes,
}) => {
  const confirmar = useConfirmar();
  /* O card inteiro leva às transações do método; editar é o ✎ ao lado do ✕.
     Sem quem filtre (fora do App), o toque continua editando. */
  const aoTocar = (method: PaymentMethod) =>
    onVerTransacoes ? onVerTransacoes(method.id) : openEdit(method);
  const botaoEditar = (method: PaymentMethod, extra = '') => (
    <button
      type="button"
      className={`pm-btn-edit${extra}`}
      onClick={e => { e.stopPropagation(); openEdit(method); }}
      title={`Editar ${method.name}`}
      aria-label={`Editar ${method.name}`}
    >
      ✎
    </button>
  );
  const estreita = useTelaEstreita();
  const [methods, setMethods] = useState<PaymentMethod[]>(metodosIniciais ?? []);
  /* O erro do servidor vai para o toast; ver utils/mensagemDeErro.ts. */
  const [erroToast, setErroToast] = useState<string | null>(null);
  const [spending, setSpending] = useState<Record<string, number>>(() =>
    (gastoGuardado?.chave === chaveDoGasto(metodosIniciais ?? [], selectedMonth?.key) ? gastoGuardado.gasto : {}));
  /*
   * "Carregando" só quando não há o que mostrar. Ela reclamou em 09/10 que a
   * seção "fica toda hora recarregando" ao voltar para ela: a lista sumia e
   * dava lugar ao aviso a cada visita e a cada mudança em outra seção, mesmo
   * com os dados na mão. Agora a lista fica, e os números se atualizam nela.
   */
  const [loading, setLoading] = useState((metodosIniciais ?? []).length === 0);
  const temLista = useRef((metodosIniciais ?? []).length > 0);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [noLimitOpen, setNoLimitOpen] = useState(false);
  /** O campo de escrever está em foco: é quando a nota do que é opcional aparece. */
  const [campoEmFoco, setCampoEmFoco] = useState(false);

  const activeMonthLabel = selectedMonth
    ? selectedMonth.label
    : (() => {
        const now = new Date();
        const label = now.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
        return label.charAt(0).toUpperCase() + label.slice(1);
      })();

  const load = useCallback(async (monthKey?: string) => {
    if (!temLista.current) setLoading(true);
    try {
      let year: number | undefined;
      let month: number | undefined;
      if (monthKey) {
        const [y, m] = monthKey.split('-').map(Number);
        year = y;
        month = m;
      }
      const [methodsData, spendingData] = await Promise.all([
        getPaymentMethods(),
        getPaymentMethodsSpending(year, month),
      ]);
      setMethods(methodsData);
      setSpending(spendingData);
      temLista.current = true;
      gastoGuardado = { chave: chaveDoGasto(methodsData, monthKey), gasto: spendingData };
    } catch (err) {
      console.error('Erro ao carregar métodos de pagamento:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(selectedMonth?.key);
  }, [load, refreshTrigger, selectedMonth]);

  /*
   * A fatura do mês — marcar como paga (15/09).
   *
   * Decisão dela: toda compra num cartão com dia de pagamento fica "aguardando"
   * até a fatura do mês ser dada como paga. O mês é o que a seção está
   * mostrando; sem seleção, o atual.
   */
  const mesDaFatura = selectedMonth?.key ?? (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();
  const [faturaOcupada, setFaturaOcupada] = useState<string | null>(null);

  const alternarFatura = async (method: PaymentMethod) => {
    const paga = !!method.faturasPagas?.includes(mesDaFatura);
    setFaturaOcupada(method.id);
    try {
      await (paga ? desmarcarFaturaPaga : marcarFaturaPaga)(method.id, mesDaFatura);
      await load(selectedMonth?.key);
      onMethodsChange?.();
    } catch (e) {
      setErroToast(mensagemDeErro(e, 'Não consegui marcar a fatura.'));
    } finally {
      setFaturaOcupada(null);
    }
  };

  const botaoDeFatura = (method: PaymentMethod) => {
    if (!method.billingDay) return null;
    const paga = !!method.faturasPagas?.includes(mesDaFatura);
    const pagoPeloBanco = method.pagoPeloBanco?.[mesDaFatura] ?? 0;
    return (
      <button
        type="button"
        className={`pm-fatura${paga ? ' pm-fatura-paga' : ''}`}
        disabled={faturaOcupada === method.id}
        onClick={e => { e.stopPropagation(); alternarFatura(method); }}
        title={paga ? 'Toque para desmarcar' : 'As compras deste cartão ficam aguardando até a fatura ser paga'}
      >
        {paga ? `Fatura de ${rotuloDoMes(mesDaFatura)} paga ✓` : `Marcar fatura de ${rotuloDoMes(mesDaFatura)} como paga`}
        {/* Pagamento parcial vindo do banco: vinculado, mas a fatura segue em aberto. */}
        {!paga && pagoPeloBanco > 0 && (
          <span className="pm-fatura-parcial">
            Pago {formatCurrency(pagoPeloBanco)} de {formatCurrency(spending[method.id] || 0)} pelo banco
          </span>
        )}
      </button>
    );
  };

  /* Delega para `brlOrMasked`: é o que faz o olhinho de "Ocultar valores"
     alcançar esta seção. Antes era um `Intl.NumberFormat` próprio, e como não
     havia um lugar por onde todo dinheiro passasse, o olhinho só chegava nos
     quatro cartões do topo. */
  const formatCurrency = (value: number) => brlOrMasked(value, !!hideValues);

  /* ── O campo único ─────────────────────────────────────────────────── */

  const [texto, setTexto] = useState('');
  const [lendo, setLendo] = useState(false);
  const [avisoDoCampo, setAvisoDoCampo] = useState('');
  /**
   * O aviso que vive DENTRO do formulário.
   *
   * `avisoDoCampo` é renderizado no bloco `{!showForm && ...}`, então ele some
   * exatamente quando o formulário abre — e é aí que o aviso do teto de IA
   * precisa estar visível, porque é ele que explica por que o formulário
   * apareceu sozinho.
   */
  const [avisoNoForm, setAvisoNoForm] = useState('');

  /**
   * Ler a frase ABRE O FORMULÁRIO preenchido — não grava direto.
   *
   * As outras seções fazem igual, e o motivo é o mesmo: o que a IA entendeu
   * precisa passar pelos olhos dela antes de virar dado. Aqui pesa mais, porque
   * "limite de 8 mil" e "fecha dia 8" são a confusão que o próprio prompt
   * avisa que existe — um cartão com limite de R$ 8 dispararia alerta em toda
   * compra.
   *
   * E de quebra resolve o resto do pedido: quem preferir o formulário continua
   * tendo o formulário, porque é nele que a frase desemboca.
   */
  const lerFrase = async () => {
    const t = texto.trim();
    if (!t || lendo) return;
    setLendo(true);
    setAvisoDoCampo('');
    try {
      const { method, problem } = await parseQuickMethod(t);
      if (!method) {
        setAvisoDoCampo(problem ?? 'Não identifiquei um método nessa frase.');
        return;
      }
      setEditingId(null);
      setFormData({
        name: method.name,
        monthlyLimit: method.monthlyLimit !== null ? String(method.monthlyLimit) : '',
        billingDay: method.billingDay !== null ? String(method.billingDay) : '',
        closingDay: method.closingDay != null ? String(method.closingDay) : '',
        /*
         * Método lido de uma frase é método NOVO, e nasce pessoal.
         *
         * A frase descreve nome, limite e fatura — nunca quem mais pode ver.
         * Deduzir "da casa" de um texto solto marcaria como público algo que
         * ninguém pediu para tornar público, e a marca é a parte que não se
         * desfaz depois de alguém ter visto.
         */
        daCasa: false,
        // A frase não diz a bandeira; ela escolhe no formulário, se quiser.
        bandeira: '',
        nivel: '',
        contaPrograma: '',
        contaNivel: '',
        ...SEM_PONTOS,
      });
      setFormError('');
      setShowForm(true);
      setTexto('');
    } catch (e) {
      /*
       * Teto de IA não é beco sem saída — o formulário nunca dependeu dela.
       *
       * Antes a tela mostrava "Muitas leituras por IA seguidas. Tente de novo
       * em 50 min" e parava. Verdadeiro e inútil: descreve o limite do sistema
       * e não diz que o caminho que ela queria continua aberto do lado.
       *
       * ── Por que o nome só às vezes vem preenchido ─────────────────────
       *
       * A primeira versão jogava a frase inteira no campo Nome. Vendo na tela,
       * "cartao inter limite 5 mil fecha dia 8" dentro de um campo chamado
       * *Nome* não parece um rascunho para ajustar: parece que o app entendeu
       * errado. E é o primeiro campo, com foco — um Enter distraído grava um
       * método com esse nome.
       *
       * Então só entra quando a frase JÁ é um nome: curta e sem número. "pix",
       * "nubank", "cartão do itaú" entram; frase com limite e dia, não —
       * porque separar isso é exatamente o trabalho que a IA faria e não fez.
       *
       * `texto` NÃO é limpo. Fechando o formulário, a frase ainda está lá para
       * tentar de novo quando o teto passar — e nada do que ela escreveu se
       * perde no caminho.
       */
      if (ehTetoDeIa(e)) {
        const pareceNome = t.length <= 30 && !/\d/.test(t);
        setEditingId(null);
        setFormData({ ...EMPTY_FORM, name: pareceNome ? t.slice(0, LIMITES.nome) : '' });
        setFormError('');
        setAvisoNoForm(AVISO_TETO_DE_IA);
        setShowForm(true);
        return;
      }
      setAvisoDoCampo(mensagemDeErro(e, 'Não consegui ler essa frase agora.'));
    } finally {
      setLendo(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormError('');
    setAvisoNoForm('');
    setShowForm(true);
  };

  const openEdit = (method: PaymentMethod) => {
    setEditingId(method.id);
    setFormData({
      name: method.name,
      monthlyLimit: method.monthlyLimit !== null ? String(method.monthlyLimit) : '',
      billingDay: method.billingDay !== null ? String(method.billingDay) : '',
      closingDay: method.closingDay != null ? String(method.closingDay) : '',
      daCasa: method.escopoPadrao === 'casa',
      bandeira: method.cardBrand ?? '',
      nivel: method.cardLevel ?? '',
      contaPrograma: method.accountProgram ?? '',
      contaNivel: method.accountLevel ? String(method.accountLevel) : '',
      ...pontosNoFormulario(method),
    });
    setFormError('');
    setAvisoNoForm('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormError('');
    setAvisoNoForm('');
  };

  /*
   * O voltar do celular fecha ESTE formulário antes de perguntar se quer sair.
   *
   * O relato foi exato: "quando estou com um formulário pra preencher, com o
   * teclado em evidência, e clico em voltar, queria que o form fechasse".
   * Só os modais e os menus estavam na pilha — os formulários que vivem DENTRO
   * da seção não, então voltar deles caía direto na pergunta de sair.
   */
  useFechavelComVoltar(showForm, closeForm);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormError('O nome é obrigatório.');
      return;
    }

    const limit =
      formData.monthlyLimit !== '' ? parseFloat(formData.monthlyLimit) : null;

    if (formData.monthlyLimit !== '' && (isNaN(limit!) || limit! < 0)) {
      setFormError('Limite inválido. Digite um número positivo ou deixe em branco.');
      return;
    }

    const billingDay = formData.billingDay !== '' ? parseInt(formData.billingDay) : null;
    if (formData.billingDay !== '' && (isNaN(billingDay!) || billingDay! < 1 || billingDay! > 31)) {
      setFormError('Dia de pagamento inválido. Digite um número entre 1 e 31.');
      return;
    }

    const closingDay = formData.closingDay !== '' ? parseInt(formData.closingDay) : null;
    if (formData.closingDay !== '' && (isNaN(closingDay!) || closingDay! < 1 || closingDay! > 31)) {
      setFormError('Dia de fechamento inválido. Digite um número entre 1 e 31.');
      return;
    }
    // Fechamento sem dia de pagamento não tem para onde mandar a compra: a regra
    // da fatura só existe com o vencimento. Dizer agora, em vez de gravar um
    // campo que não faria efeito nenhum.
    if (closingDay !== null && billingDay === null) {
      setFormError('Preencha também o dia de pagamento: o fechamento só vale junto com ele.');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await updatePaymentMethod(editingId, { name: formData.name.trim(), monthlyLimit: limit, billingDay, closingDay });
        await aplicarMarcaDaCasa(editingId);
        // Só quando ELA mudou: salvar o nome não regrava a bandeira que o banco informou.
        const antes = methods.find(m => m.id === editingId);
        if ((antes?.cardBrand ?? '') !== formData.bandeira || (antes?.cardLevel ?? '') !== formData.nivel) {
          await definirCartaoDoMetodo(editingId, formData.bandeira || null, formData.nivel || null);
        }
        // E o nível da conta do banco: só quando ela mexeu. Programa sem nível não grava.
        const nivelDaConta = programaDaConta(formData.contaPrograma) && formData.contaNivel ? Number(formData.contaNivel) : null;
        if ((antes?.accountProgram ?? '') !== (nivelDaConta ? formData.contaPrograma : '')
            || (antes?.accountLevel ?? null) !== nivelDaConta) {
          await definirContaDoMetodo(editingId, nivelDaConta ? formData.contaPrograma : null, nivelDaConta)
            // Servidor que ainda não guarda o nível responde 503: o resto do método já foi salvo.
            .catch(() => undefined);
        }
        // Idem para a pontuação: só regrava quando ela mexeu.
        const taxa = numeroDoCampo(formData.pontos);
        const alta = numeroDoCampo(formData.pontosAlta);
        const fatura = numeroDoCampo(formData.pontosFatura);
        // Compara com o que o campo MOSTRAVA: a publicada pelo banco não vira
        // "informada" só porque ela salvou o nome do cartão.
        const mostrava = antes ? pontosNoFormulario(antes) : SEM_PONTOS;
        if (numeroDoCampo(mostrava.pontos) !== taxa || mostrava.pontosMoeda !== formData.pontosMoeda
            || numeroDoCampo(mostrava.pontosAlta) !== alta || numeroDoCampo(mostrava.pontosFatura) !== fatura) {
          await definirPontosDoMetodo(editingId, { taxa, moeda: formData.pontosMoeda, taxaAlta: alta, faturaMinima: fatura })
            // Servidor que ainda não guarda pontos responde 503: o resto do método já foi salvo.
            .catch(() => undefined);
        }
      } else {
        const criado = await createPaymentMethod({ name: formData.name.trim(), monthlyLimit: limit, billingDay, closingDay });
        // Método novo não tem passado, então nunca há o que perguntar.
        if (formData.daCasa) await definirEscopoDoMetodo(criado.id, 'casa');
        if (formData.bandeira) await definirCartaoDoMetodo(criado.id, formData.bandeira, formData.nivel || null);
        if (programaDaConta(formData.contaPrograma) && formData.contaNivel) {
          await definirContaDoMetodo(criado.id, formData.contaPrograma, Number(formData.contaNivel)).catch(() => undefined);
        }
        if (numeroDoCampo(formData.pontos)) {
          await definirPontosDoMetodo(criado.id, {
            taxa: numeroDoCampo(formData.pontos), moeda: formData.pontosMoeda,
            taxaAlta: numeroDoCampo(formData.pontosAlta), faturaMinima: numeroDoCampo(formData.pontosFatura),
          }).catch(() => undefined);
        }
      }
      await load(selectedMonth?.key);
      setVersaoDosPontos(v => v + 1);
      onMethodsChange?.();
      closeForm();
    } catch (err) {
      setFormError(mensagemDeErro(err, 'Não consegui salvar este método.'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * A marca da casa: primeiro o padrão, depois — e só depois — o passado.
   *
   * ── Por que são dois passos, e não um ────────────────────────────────────
   *
   * Mudar o padrão é inofensivo: vale do próximo lançamento em diante. Marcar o
   * histórico é a única coisa desta feature que torna público o que era
   * privado, e um clique dela publica meses de gastos de uma vez.
   *
   * Então o padrão muda direto, e o passado vira uma pergunta com o NÚMERO na
   * frente. Quem responde "agora não" fica com o método marcado e o histórico
   * intacto — que é um estado legítimo, não um meio-caminho.
   *
   * ── E por que perguntar mesmo quando o alvo é `pessoal` ─────────────────
   *
   * Desmarcar também mexe no passado, na direção oposta: some da vista de quem
   * já enxergava. É menos grave e continua sendo uma mudança que a pessoa
   * merece ver antes, com o mesmo número.
   */
  const aplicarMarcaDaCasa = async (id: string) => {
    const alvo = formData.daCasa ? 'casa' : 'pessoal';
    const original = methods.find(m => m.id === id)?.escopoPadrao ?? 'pessoal';
    if (alvo === original) return;

    const { historico } = await definirEscopoDoMetodo(id, alvo);
    if (historico === 0) return;

    const um = historico === 1;
    const ok = await confirmar({
      titulo: alvo === 'casa' ? 'Marcar também o que já passou?' : 'Tirar também o que já passou?',
      alvo: formData.name.trim(),
      texto: alvo === 'casa'
        ? `${historico} ${um ? 'lançamento anterior' : 'lançamentos anteriores'} deste método `
          + `${um ? 'continua' : 'continuam'} como ${um ? 'seu' : 'seus'}. `
          + `Marcar ${um ? 'ele' : 'eles'} como da casa faz `
          + `quem está na sua casa passar a ver ${um ? 'esse gasto' : 'esses gastos'}. `
          + 'Dá para desfazer aqui mesmo depois.'
        : `${historico} ${um ? 'lançamento anterior' : 'lançamentos anteriores'} deste método `
          + `${um ? 'está marcado' : 'estão marcados'} como da casa. `
          + `Tirar a marca faz ${um ? 'ele sumir' : 'eles sumirem'} da visão de quem está na sua casa.`,
      acao: alvo === 'casa' ? `Marcar ${historico}` : `Tirar ${historico}`,
      perigo: alvo === 'casa',
    });
    if (!ok) return;

    await marcarHistoricoDoMetodo(id, alvo);
  };

  /**
   * Excluir método, com a consequência na frente.
   *
   * O diálogo antigo prometia "as transações já salvas não serão afetadas" — e
   * o delete falhava JUSTAMENTE porque elas não eram afetadas: existe FK de
   * `transactions.payment_method` para cá. A tela prometia o que o banco
   * proibia, e o erro voltava em inglês.
   *
   * Agora a primeira tentativa não força nada. Se houver lançamentos usando, o
   * servidor devolve 409 com a contagem e ela decide de novo, sabendo que eles
   * ficarão sem método — o que os tira do gasto por cartão.
   */
  const handleDelete = async (method: PaymentMethod) => {
    if (!await confirmar({
      titulo: 'Excluir este método?',
      alvo: method.name,
      texto: 'Os lançamentos feitos com ele continuam, só ficam sem método.',
      acao: 'Excluir', perigo: true,
    })) return;
    try {
      await deletePaymentMethod(method.id);
    } catch (err: any) {
      const emUso = err?.response?.data?.inUse;
      if (err?.response?.status === 409 && emUso) {
        const ok = await confirmar({
          titulo: 'Este método está em uso',
          alvo: method.name,
          texto: `${emUso} ${emUso === 1 ? 'lançamento usa' : 'lançamentos usam'} este método. `
            + `Excluir mesmo assim deixa ${emUso === 1 ? 'esse lançamento' : 'esses lançamentos'} `
            + 'sem método de pagamento, mas nenhum é apagado.',
          acao: 'Excluir mesmo assim', perigo: true,
        });
        if (!ok) return;
        try {
          await deletePaymentMethod(method.id, true);
        } catch (e2) {
          setErroToast(mensagemDeErro(e2, 'Não consegui excluir o método.'));
          return;
        }
      } else {
        setErroToast(mensagemDeErro(err, 'Não consegui excluir o método.'));
        return;
      }
    }
    await load(selectedMonth?.key);
    onMethodsChange?.();
  };

  /*
   * Qualquer método pode ser excluído, inclusive os que nascem com a conta —
   * menos o último. O servidor recusa do mesmo jeito; esconder o botão aqui é
   * só não oferecer o que não vai funcionar.
   */
  const podeExcluir = methods.length > 1;
  const methodsWithLimit = methods.filter(m => m.monthlyLimit !== null);

  /**
   * Sem limite, ordenados pelo que pesa — e os zerados no fim.
   *
   * A ordem antiga era a do banco (nome), que não responde nenhuma pergunta.
   * Numa seção de consulta, a primeira linha deve ser a que mais importa, e
   * um método sem gasto no mês não compete com um de R$ 842.
   */
  const methodsWithoutLimit = methods
    .filter(m => m.monthlyLimit === null)
    .sort((a, b) => (spending[b.id] || 0) - (spending[a.id] || 0));
  /**
   * Sem limite, mas com gasto no mês: ganham card, como os de limite.
   *
   * Pedido dela (06/10): a visibilidade dos cards é boa, e o método que ela
   * USA merece a mesma. A lista compacta continua para os parados — seis
   * linhas de "—" em formato de card seriam seis caixas vazias.
   */
  const emUsoSemLimite = methodsWithoutLimit.filter(m => (spending[m.id] || 0) > 0);
  const paradosSemLimite = methodsWithoutLimit.filter(m => (spending[m.id] || 0) === 0);
  /** Usa métodos sem limite e não definiu limite em nenhum: "Com limite" vira uma linha. */
  const soSemLimiteEmUso = methodsWithLimit.length === 0 && emUsoSemLimite.length > 0;
  /** Sobe a cada método salvo, para o bloco de pontos reler a estimativa. */
  const [versaoDosPontos, setVersaoDosPontos] = useState(0);
  /** O cartão cujas vantagens estão abertas, ou `null`. */
  const [vantagensDe, setVantagensDe] = useState<PaymentMethod | null>(null);
  /**
   * "Meu banco não está na lista": o que ela escreveu, e se o pedido já saiu.
   * A lista de programas é feita à mão e cresce pelo que as pessoas pedem.
   */
  const [pedidoDeConta, setPedidoDeConta] = useState<{ banco: string; nivel: string; estado: 'escrevendo' | 'enviando' | 'enviado' | 'falhou' }>(
    { banco: '', nivel: '', estado: 'escrevendo' });
  const enviarPedidoDeConta = async () => {
    setPedidoDeConta(p => ({ ...p, estado: 'enviando' }));
    try {
      await pedirProgramaDaConta(pedidoDeConta.banco.trim(), pedidoDeConta.nivel.trim());
      setPedidoDeConta({ banco: '', nivel: '', estado: 'enviado' });
    } catch {
      setPedidoDeConta(p => ({ ...p, estado: 'falhou' }));
    }
  };
  /** A conta cujas vantagens estão abertas, ou `null`. */
  const [vantagensDaContaDe, setVantagensDaContaDe] = useState<PaymentMethod | null>(null);
  /*
   * O selo da conta do banco (13/10): aparece no método em que ela informou o
   * programa e o nível, e abre o que o banco oferece ali. Um botão, para o
   * clique não abrir também a edição do método, que é do card inteiro.
   */
  const seloDaConta = (method: PaymentMethod) => (
    nomeDaConta(method.accountProgram, method.accountLevel) ? (
      <button
        type="button" className="pm-badge-cartao"
        title="Ver as vantagens desta conta"
        onClick={e => { e.stopPropagation(); setVantagensDaContaDe(method); }}
      >
        {nomeDaConta(method.accountProgram, method.accountLevel)} · vantagens
      </button>
    ) : null
  );

  return (
    <div className="pm-container">
      <Toast message={erroToast} onDismiss={() => setErroToast(null)} />
      {vantagensDaContaDe && (
        <VantagensDaConta
          nome={vantagensDaContaDe.name} programa={vantagensDaContaDe.accountProgram}
          nivel={vantagensDaContaDe.accountLevel} onClose={() => setVantagensDaContaDe(null)}
        />
      )}
      {vantagensDe && (
        <VantagensDoCartao
          nome={vantagensDe.name} bandeira={vantagensDe.cardBrand} nivel={vantagensDe.cardLevel}
          onClose={() => setVantagensDe(null)}
        />
      )}
      {/*
        Sem "+ Novo método" aqui.

        Ele fazia a mesma coisa que "Preencher manualmente" logo abaixo do
        campo, e dois botões para a mesma ação em quinze centímetros de tela
        obrigam a escolher entre caminhos idênticos. As outras cinco seções com
        campo único já resolvem assim: o campo manda, e o formulário é a saída
        de baixo dele.
      */}
      <div className="pm-header">
        <h2>Métodos de Pagamento</h2>
      </div>

      {!showForm && (
        <div className="pm-qe">
          <div className="qe-input-row">
            <input
              className="qe-input"
              value={texto}
              onChange={e => setTexto(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lerFrase(); } }}
              onFocus={() => setCampoEmFoco(true)}
              onBlur={() => setCampoEmFoco(false)}
              placeholder={estreita ? 'nubank, limite 3 mil' : 'nubank com limite de 3 mil, fecha dia 8'}
              disabled={lendo}
            />
            <button className="qe-go" onClick={lerFrase} disabled={lendo || !texto.trim()} aria-busy={lendo} aria-label="Ler frase">
              →
            </button>
          </div>

          {/*
            As dicas nomeiam o que é OPCIONAL.
            
            Só o nome é obrigatório — limite e dia da fatura o app aceita em
            branco, e sem dizer isso a pessoa acha que precisa saber os três
            para começar. Cada exemplo mostra um nível: só o nome, nome com
            limite, e os três juntos.
          */}
          <div className="pm-qe-exemplos">
            {['pix', 'nubank com limite de 3 mil', 'itaú limite 5 mil, fecha dia 12'].map(ex => (
              <button key={ex} className="qe-hint" onClick={() => setTexto(ex)}>{ex}</button>
            ))}
          </div>

          {/*
            Só quando ela vai escrever (pedido dela, 06/10). A nota responde a
            uma dúvida de quem está preenchendo; para quem só veio olhar os
            cartões, era uma linha a mais antes do conteúdo.

            Fica também enquanto há texto no campo: tocar num exemplo ou no
            botão tira o foco, e a explicação não pode sumir justo aí.
          */}
          {(campoEmFoco || texto.trim() !== '') && (
            <p className="pm-qe-nota">
              Só o <strong>nome</strong> é obrigatório. <em>Limite</em> e{' '}
              <em>dia da fatura</em> são opcionais, dá para escrever depois.
            </p>
          )}

          {avisoDoCampo && <p className="pm-form-error">{avisoDoCampo}</p>}

          <button className="qe-manual-fora" onClick={openAdd}>Preencher manualmente</button>
        </div>
      )}

      {selectedMonth && (
        <div className="pm-month-badge">
          <span>📅 Exibindo dados de: <strong>{selectedMonth.label}</strong></span>
        </div>
      )}

      {showForm && (
        <form className="pm-form" onSubmit={handleSave}>
          <h3>{editingId ? 'Editar método' : 'Novo método'}</h3>

          {/* Não é erro, e não pode parecer um: o formulário está funcionando.
              Por isso classe própria em vez de `pm-form-error`, que é
              vermelho — vermelho aqui diria que algo deu errado com ela. */}
          {avisoNoForm && <p className="pm-form-aviso">{avisoNoForm}</p>}
          <div className="pm-form-row">
            <div className="pm-form-group">
              <label>Nome *</label>
              <input
                type="text"
                maxLength={LIMITES.nome}
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="Ex: Nubank, Vale Alimentação..."
                autoFocus
              />
            </div>
            <div className="pm-form-group">
              <label>Limite mensal de gastos (R$)</label>
              <input
                type="number"
                value={formData.monthlyLimit}
                onChange={e => setFormData(p => ({ ...p, monthlyLimit: e.target.value }))}
                placeholder="Deixe vazio para sem limite"
                min="0"
                step="0.01"
              />
            </div>
            <div className="pm-form-group">
              <label>
                Dia de pagamento da fatura <span className="pm-optional">(opcional)</span>
                <span className="pm-tooltip-wrap">
                  <span className="pm-tooltip-icon">?</span>
                  <span className="pm-tooltip-text">
                    Ao registrar uma transação, você pode usar este dia como a data de pagamento, o dia em que o valor de fato sai da sua conta.
                  </span>
                </span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.billingDay}
                onChange={e => {
                  const raw = e.target.value.replace(/\D/g, '');
                  if (raw === '') { setFormData(p => ({ ...p, billingDay: '' })); return; }
                  const num = parseInt(raw, 10);
                  if (num >= 1 && num <= 31) setFormData(p => ({ ...p, billingDay: String(num) }));
                }}
                placeholder="Ex: 10"
                maxLength={2}
              />
            </div>
            <div className="pm-form-group">
              <label>
                Dia de fechamento da fatura <span className="pm-optional">(opcional)</span>
                <span className="pm-tooltip-wrap">
                  <span className="pm-tooltip-icon">?</span>
                  <span className="pm-tooltip-text">
                    Compras feitas a partir deste dia entram na fatura do mês seguinte. Sem ele, toda compra no cartão vai para o pagamento do mês seguinte à compra.
                  </span>
                </span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.closingDay}
                onChange={e => {
                  const raw = e.target.value.replace(/\D/g, '');
                  if (raw === '') { setFormData(p => ({ ...p, closingDay: '' })); return; }
                  const num = parseInt(raw, 10);
                  if (num >= 1 && num <= 31) setFormData(p => ({ ...p, closingDay: String(num) }));
                }}
                placeholder="Ex: 3"
                maxLength={2}
              />
            </div>
          </div>
          {/*
            * A bandeira e o nível do cartão: é o que define as vantagens que a
            * bandeira oferece. Quem conecta o banco recebe os dois sozinho;
            * aqui escolhe quem não conecta. Opcional, e depois de tudo que é
            * conta: método que não é cartão passa direto.
            */}
          <div className="pm-form-row">
            <div className="pm-form-group">
              <label>Bandeira do cartão <span className="pm-optional">(opcional)</span></label>
              <select
                value={formData.bandeira}
                onChange={e => setFormData(p => ({ ...p, bandeira: e.target.value, nivel: '' }))}
              >
                <option value="">Não é cartão, ou não sei</option>
                {BANDEIRAS_E_NIVEIS.map(b => (
                  <option key={b.bandeira} value={b.bandeira}>{nomeDoCartao(b.bandeira, null)}</option>
                ))}
                {/* O banco pode informar uma bandeira que não está na lista: ela continua escolhida. */}
                {formData.bandeira && !BANDEIRAS_E_NIVEIS.some(b => b.bandeira === formData.bandeira) && (
                  <option value={formData.bandeira}>{nomeDoCartao(formData.bandeira, null)}</option>
                )}
              </select>
            </div>
            {formData.bandeira && (
              <div className="pm-form-group">
                <label>Nível <span className="pm-optional">(está escrito no cartão)</span></label>
                <select value={formData.nivel} onChange={e => setFormData(p => ({ ...p, nivel: e.target.value }))}>
                  <option value="">Não sei</option>
                  {(BANDEIRAS_E_NIVEIS.find(b => b.bandeira === formData.bandeira)?.niveis ?? []).map(n => (
                    <option key={n} value={n}>{nomeDoCartao(null, n)}</option>
                  ))}
                  {formData.nivel && !(BANDEIRAS_E_NIVEIS.find(b => b.bandeira === formData.bandeira)?.niveis ?? []).includes(formData.nivel) && (
                    <option value={formData.nivel}>{nomeDoCartao(null, formData.nivel)}</option>
                  )}
                </select>
              </div>
            )}
          </div>
          {/*
            * A conta do banco: o programa de relacionamento e o nível dela nele
            * (13/10). É o que define as vantagens que o banco oferece naquela
            * conta. O nível só ela sabe: aparece no app do banco. Opcional.
            */}
          <div className="pm-form-row">
            <div className="pm-form-group">
              <label>Programa da conta do banco <span className="pm-optional">(opcional)</span></label>
              <select
                value={formData.contaPrograma}
                onChange={e => {
                  const escolhido = programaDaConta(e.target.value);
                  // Segmento sem níveis: o nível único entra sozinho.
                  setFormData(p => ({ ...p, contaPrograma: e.target.value, contaNivel: escolhido && semNiveis(escolhido) ? '1' : '' }));
                  if (e.target.value === FORA_DA_LISTA) setPedidoDeConta({ banco: '', nivel: '', estado: 'escrevendo' });
                }}
              >
                <option value="">Não tem, ou não sei</option>
                {PROGRAMAS_DA_CONTA.map(p => (
                  <option key={p.id} value={p.id}>{semNiveis(p) ? p.nome : `${p.nome} · ${p.programa}`}</option>
                ))}
                <option value={FORA_DA_LISTA}>Meu banco não está na lista</option>
              </select>
            </div>
            {formData.contaPrograma === FORA_DA_LISTA && (
              <div className="pm-form-group">
                <label>Qual é o banco e o seu nível?</label>
                {pedidoDeConta.estado === 'enviado' ? (
                  <p className="pm-pedido-ok">Anotado. Vamos ler a página do banco e incluir na lista.</p>
                ) : (
                  <>
                    <input
                      type="text" maxLength={LIMITES.nome} placeholder="Ex: Bradesco Prime"
                      value={pedidoDeConta.banco}
                      onChange={e => setPedidoDeConta(p => ({ ...p, banco: e.target.value, estado: 'escrevendo' }))}
                    />
                    <input
                      type="text" maxLength={LIMITES.nome} placeholder="Nível ou tipo de conta (se tiver)"
                      value={pedidoDeConta.nivel} style={{ marginTop: 6 }}
                      onChange={e => setPedidoDeConta(p => ({ ...p, nivel: e.target.value, estado: 'escrevendo' }))}
                    />
                    <button
                      type="button" className="pm-pedido-botao"
                      disabled={pedidoDeConta.banco.trim().length < 2 || pedidoDeConta.estado === 'enviando'}
                      aria-busy={pedidoDeConta.estado === 'enviando'}
                      onClick={enviarPedidoDeConta}
                    >
                      Pedir para incluir
                    </button>
                    {pedidoDeConta.estado === 'falhou' && <p className="pm-pedido-erro">Não consegui anotar agora. Tente de novo.</p>}
                  </>
                )}
              </div>
            )}
            {programaDaConta(formData.contaPrograma) && !semNiveis(programaDaConta(formData.contaPrograma)!) && (
              <div className="pm-form-group">
                <label>Seu nível <span className="pm-optional">(está no app do banco)</span></label>
                <select value={formData.contaNivel} onChange={e => setFormData(p => ({ ...p, contaNivel: e.target.value }))}>
                  <option value="">Escolha o nível</option>
                  {(programaDaConta(formData.contaPrograma)?.niveis ?? []).map(n => (
                    <option key={n} value={String(n)}>Nível {n}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {/*
            * Quanto o cartão pontua. Com isso o app soma os pontos de cada
            * compra e o saldo do programa anda sozinho (`SeusPontos`). Depende
            * do produto do cartão, então quem informa é ela. A pontuação
            * acelerada só aparece depois da base: é exceção (o Itaú Uniclass
            * Black dá 2,0, e 2,5 nos meses de fatura a partir de R$ 4 mil).
            */}
          <div className="pm-form-row">
            <div className="pm-form-group">
              <label>Pontos que o cartão dá <span className="pm-optional">(opcional)</span></label>
              <input
                inputMode="decimal" value={formData.pontos} placeholder="2,2"
                onChange={e => setFormData(p => ({ ...p, pontos: e.target.value }))}
              />
              {/* De onde veio o número que já está no campo: ela não digitou. */}
              {(() => {
                const m = methods.find(x => x.id === editingId);
                return m && !m.pontosTaxa && m.pontuacaoPublicada
                  && formData.pontos === String(m.pontuacaoPublicada.taxa).replace('.', ',')
                  ? <small className="pm-pontos-origem">É o que o banco publica para este cartão. Mude se o seu for diferente.</small>
                  : null;
              })()}
            </div>
            <div className="pm-form-group">
              <label>A cada</label>
              <select
                value={formData.pontosMoeda}
                onChange={e => setFormData(p => ({ ...p, pontosMoeda: e.target.value === 'BRL' ? 'BRL' : 'USD' }))}
              >
                <option value="USD">1 dólar gasto</option>
                <option value="BRL">1 real gasto</option>
              </select>
            </div>
          </div>
          {formData.pontos.trim() && (
            <div className="pm-form-row">
              <div className="pm-form-group">
                <label>Pontuação acelerada <span className="pm-optional">(se o cartão tiver)</span></label>
                <input
                  inputMode="decimal" value={formData.pontosAlta} placeholder="2,5"
                  onChange={e => setFormData(p => ({ ...p, pontosAlta: e.target.value }))}
                />
              </div>
              <div className="pm-form-group">
                <label>Com fatura a partir de (R$)</label>
                <input
                  inputMode="decimal" value={formData.pontosFatura} placeholder="4000"
                  onChange={e => setFormData(p => ({ ...p, pontosFatura: e.target.value }))}
                />
              </div>
            </div>
          )}
          {/*
            * A marca da casa fica FORA da linha dos outros campos.
            *
            * Nome, limite e dia de fatura são ajustes de conta. Este decide
            * quem, além dela, enxerga o que passa neste método — e um checkbox
            * dessa consequência espremido ao lado de "dia da fatura" seria
            * marcado sem leitura.
            */}
          <label className="pm-casa">
            <input
              type="checkbox"
              checked={formData.daCasa}
              onChange={e => setFormData(p => ({ ...p, daCasa: e.target.checked }))}
            />
            <span>
              <strong>Este método é da casa</strong>
              <span className="pm-casa-ajuda">
                Tudo que passar nele nasce como gasto da casa, e quem está na sua
                casa vê. O que você lançar em outros métodos continua só seu.
              </span>
            </span>
          </label>

          {formError && <p className="pm-form-error">{formError}</p>}
          <div className="pm-form-actions">
            <button type="button" className="pm-btn-cancel" onClick={closeForm}>
              Cancelar
            </button>
            <button type="submit" className="pm-btn-save" disabled={saving} aria-busy={saving}>
              {editingId ? 'Atualizar' : 'Adicionar'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="loading-wrap">
          <div className="loading-ring" />
          <span className="loading-text">Carregando métodos...</span>
        </div>
      ) : (
        <>
          {/*
            A seção "Com limite" só ocupa espaço quando tem o que mostrar — ou
            quando a conta ainda não tem NADA, e o exemplo ensina o que ela vira.

            Quem usa métodos sem limite e não definiu limite em nenhum via
            aqui o título, dois cards de exemplo e uma chamada: metade da tela
            para dizer "não há", em cima dos métodos que ela usa de verdade
            (pedido dela, 06/10). Nesse caso a seção se resume a uma linha,
            embaixo dos cards em uso. O inverso já acontecia: sem método sem
            limite, a seção deles não aparece.
          */}
          {!soSemLimiteEmUso && (
          <div className="pm-section">
            {/* Sem emoji: a seção irmã abaixo não tem, e um título decorado ao
                lado de um título limpo lê como descuido, não como ênfase. */}
            <h3 className="pm-section-title">Com limite · {activeMonthLabel}</h3>
            {methodsWithLimit.length > 0 ? (
              <div className="pm-cards">
                {methodsWithLimit.map(method => {
                  const used = spending[method.id] || 0;
                  const limit = method.monthlyLimit!;
                  const pct = Math.min((used / limit) * 100, 100);
                  const remaining = Math.max(limit - used, 0);
                  const remainingPct = Math.max(100 - (used / limit) * 100, 0);
                  const over = used > limit;

                  return (
                    <div key={method.id} className={`pm-card ${over ? 'over-limit' : ''}`} onClick={() => aoTocar(method)} style={{ cursor: 'pointer' }} title={`Ver as transações de ${method.name}`}>
                      <div className="pm-card-header">
                        <div className="pm-card-name">
                          {method.name}
                          {method.isDefault && <span className="pm-badge-default">padrão</span>}
                          {nomeDoCartao(method.cardBrand, method.cardLevel) && (
                            /* O selo é um botão: abre as vantagens que a bandeira
                               publica para este cartão. Para o clique não abrir
                               também a edição do método, que é do card inteiro. */
                            <button
                              type="button" className="pm-badge-cartao"
                              title="Ver as vantagens deste cartão"
                              onClick={e => { e.stopPropagation(); setVantagensDe(method); }}
                            >
                              {nomeDoCartao(method.cardBrand, method.cardLevel)} · vantagens
                            </button>
                          )}
                          {seloDaConta(method)}
                          {method.escopoPadrao === 'casa' && (
                            <span className="pm-badge-casa" title="Os gastos deste método aparecem para quem está na sua casa">
                              da casa
                            </span>
                          )}
                        </div>
                        <div className="pm-card-acoes">
                          {botaoEditar(method)}
                          {podeExcluir && (
                            <button
                              className="pm-btn-delete"
                              onClick={e => { e.stopPropagation(); handleDelete(method); }}
                              title="Excluir"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="pm-limit-info">
                        <span className="pm-used">{formatCurrency(used)} usados</span>
                        <span className="pm-limit-total">de {formatCurrency(limit)}</span>
                      </div>

                      <div className="pm-progress-bar-bg">
                        <div
                          className={`pm-progress-bar-fill ${over ? 'over' : pct > 80 ? 'warning' : 'ok'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>

                      <div className="pm-remaining-info">
                        {over ? (
                          <span className="pm-over-text">
                            ⚠️ Limite excedido em {formatCurrency(used - limit)}
                          </span>
                        ) : (
                          <span className="pm-remaining-text">
                            Faltam{' '}
                            <strong>{formatCurrency(remaining)}</strong>
                            {' '}({formatPct(remainingPct)}) para atingir o limite
                          </span>
                        )}
                      </div>
                      {method.billingDay && (
                        <div className="pm-billing-day">
                          📅 Pagamento dia <strong>{method.billingDay}</strong>
                          {method.closingDay != null && <> · fecha dia <strong>{method.closingDay}</strong></>}
                        </div>
                      )}
                      {botaoDeFatura(method)}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-preview-wrap">
                <span className="empty-preview-badge">Exemplo</span>
                <div className="pm-cards empty-preview-ghost">
                  {[
                    { name: 'Nubank',         limit: 1500, used: 870,  billingDay: 12 },
                    { name: 'Cartão Inter',   limit: 2000, used: 1720, billingDay: 8  },
                  ].map((m, i) => {
                    const pct = Math.min((m.used / m.limit) * 100, 100);
                    const remaining = m.limit - m.used;
                    const remainingPct = 100 - pct;
                    const isWarning = pct > 80;
                    return (
                      <div key={i} className="pm-card">
                        <div className="pm-card-header">
                          <div className="pm-card-name">{m.name}</div>
                          <button className="pm-btn-delete">✕</button>
                        </div>
                        <div className="pm-limit-info">
                          <span className="pm-used">{formatCurrency(m.used)} usados</span>
                          <span className="pm-limit-total">de {formatCurrency(m.limit)}</span>
                        </div>
                        <div className="pm-progress-bar-bg">
                          <div
                            className={`pm-progress-bar-fill ${isWarning ? 'warning' : 'ok'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="pm-remaining-info">
                          <span className="pm-remaining-text">
                            Faltam <strong>{formatCurrency(remaining)}</strong>
                            {' '}({formatPct(remainingPct)}) para atingir o limite
                          </span>
                        </div>
                        <div className="pm-billing-day">
                          📅 Pagamento dia <strong>{m.billingDay}</strong>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="empty-cta">
                  <p className="empty-cta-title">Nenhum método com limite definido</p>
                  <p className="empty-cta-sub">Edite um método existente ou crie um novo e defina um limite mensal para acompanhar seus gastos.</p>
                </div>
              </div>
            )}
          </div>
          )}

          {/*
            Some quando não há nenhum método sem limite.

            Sem isso, quem acabou de se cadastrar via um cabeçalho
            "TODOS OS MÉTODOS · Agosto de 2026" com 27px de altura, nada
            embaixo e um clique que abre o vazio. A seção irmã logo acima já
            diz o que fazer — duas mensagens de vazio para o mesmo vazio é
            uma a mais, e um rótulo sozinho não é nem mensagem.
          */}
          {emUsoSemLimite.length > 0 && (
            <div className="pm-section">
              <h3 className="pm-section-title">Em uso, sem limite · {activeMonthLabel}</h3>
              <div className="pm-cards">
                {emUsoSemLimite.map(method => (
                  <div
                    key={method.id}
                    className="pm-card pm-card-sem-limite"
                    onClick={() => aoTocar(method)}
                    style={{ cursor: 'pointer' }}
                    title={`Ver as transações de ${method.name}`}
                  >
                    <div className="pm-card-header">
                      <div className="pm-card-name">
                        {method.name}
                        {method.isDefault && <span className="pm-badge-default">padrão</span>}
                          {nomeDoCartao(method.cardBrand, method.cardLevel) && (
                            /* O selo é um botão: abre as vantagens que a bandeira
                               publica para este cartão. Para o clique não abrir
                               também a edição do método, que é do card inteiro. */
                            <button
                              type="button" className="pm-badge-cartao"
                              title="Ver as vantagens deste cartão"
                              onClick={e => { e.stopPropagation(); setVantagensDe(method); }}
                            >
                              {nomeDoCartao(method.cardBrand, method.cardLevel)} · vantagens
                            </button>
                          )}
                          {seloDaConta(method)}
                        {method.escopoPadrao === 'casa' && (
                          <span className="pm-badge-casa" title="Os gastos deste método aparecem para quem está na sua casa">
                            da casa
                          </span>
                        )}
                      </div>
                      <div className="pm-card-acoes">
                        {botaoEditar(method)}
                        {podeExcluir && (
                          <button
                            className="pm-btn-delete"
                            onClick={e => { e.stopPropagation(); handleDelete(method); }}
                            title={`Excluir ${method.name}`}
                            aria-label={`Excluir ${method.name}`}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="pm-limit-info">
                      <span className="pm-used">{formatCurrency(spending[method.id] || 0)} usados</span>
                      <span className="pm-limit-total">sem limite</span>
                    </div>

                    {/* No lugar da barra: sem limite não há "quanto falta", e uma
                        barra vazia ou cheia afirmaria uma das duas coisas. */}
                    <div className="pm-remaining-info">
                      <span className="pm-remaining-text">Use o ✎ para definir um limite e acompanhar por aqui.</span>
                    </div>
                    {method.billingDay && (
                      <div className="pm-billing-day">
                        📅 Pagamento dia <strong>{method.billingDay}</strong>
                        {method.closingDay != null && <> · fecha dia <strong>{method.closingDay}</strong></>}
                      </div>
                    )}
                    {botaoDeFatura(method)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {soSemLimiteEmUso && (
            <p className="pm-secao-minima">
              {/* "Quando estiver próximo" é literal: o app avisa aos 90% do
                  limite (`PERTO_DO_LIMITE`, em `limitAlerts.ts`) e de novo
                  quando ele é atingido. */}
              Nenhum método com limite. Toque em um método acima para definir um limite e te avisamos quando ele estiver próximo.
            </p>
          )}

          {paradosSemLimite.length > 0 && (
          <div className="pm-section">
            <button
              className={`pm-section-title pm-section-toggle${noLimitOpen ? ' open' : ''}`}
              onClick={() => setNoLimitOpen(v => !v)}
              aria-expanded={noLimitOpen}
            >
              {/* O mês aparece UMA vez, aqui. Antes ele era repetido dentro de
                  cada card ("GASTO EM AGOSTO DE 2026", 6×) — seis linhas de
                  ruído dizendo o que o cabeçalho já diz. */}
              <span>
                {methodsWithLimit.length > 0 || emUsoSemLimite.length > 0 ? 'Sem uso no mês' : 'Todos os métodos'}
                <em className="pm-ref-month"> · {activeMonthLabel}</em>
              </span>
              <span className="pm-nolimit-count">{paradosSemLimite.length}</span>
              <svg className="pm-toggle-chevron" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {/* Lista, não grade de cards — para os PARADOS. Os que têm gasto
                viraram card na seção de cima (pedido dela).
                Card é um recipiente caro — borda, respiro e um rótulo repetido
                para exibir um nome e um valor. Pior: numa grade cada valor cai
                numa posição horizontal diferente, então comparar Pix com Vale
                Alimentação exige varrer a tela. Numa coluna alinhada à direita,
                com tabular-nums, a comparação é um olhar. Esta seção é para
                consultar, e consultar é comparar. */}
            <div className={`pm-ref-list${noLimitOpen ? ' pm-nolimit-open' : ''}`}>
              {paradosSemLimite.map(method => {
                const used = spending[method.id] || 0;
                return (
                  <div
                    key={method.id}
                    className={`pm-ref-row${used === 0 ? ' pm-ref-zero' : ''}`}
                    onClick={() => aoTocar(method)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter') aoTocar(method); }}
                    title={`Ver as transações de ${method.name}`}
                  >
                    <span className="pm-ref-name">
                      {method.name}
                      {method.isDefault && <span className="pm-badge-default">padrão</span>}
                          {nomeDoCartao(method.cardBrand, method.cardLevel) && (
                            /* O selo é um botão: abre as vantagens que a bandeira
                               publica para este cartão. Para o clique não abrir
                               também a edição do método, que é do card inteiro. */
                            <button
                              type="button" className="pm-badge-cartao"
                              title="Ver as vantagens deste cartão"
                              onClick={e => { e.stopPropagation(); setVantagensDe(method); }}
                            >
                              {nomeDoCartao(method.cardBrand, method.cardLevel)} · vantagens
                            </button>
                          )}
                          {seloDaConta(method)}
                      {method.escopoPadrao === 'casa' && (
                        <span className="pm-badge-casa" title="Os gastos deste método aparecem para quem está na sua casa">
                          da casa
                        </span>
                      )}
                      {method.billingDay && (
                        <span className="pm-ref-day">· dia {method.billingDay}</span>
                      )}
                      {botaoDeFatura(method)}
                    </span>

                    {/* Neutro, não vermelho. Vermelho significa UMA coisa neste
                        app — limite estourado. Seis gastos normais em vermelho
                        faziam a seção inteira parecer seis alertas. */}
                    <span className="pm-ref-value">
                      {used > 0 ? formatCurrency(used) : <span className="pm-ref-dash" title="Sem gastos no período">—</span>}
                    </span>

                    <span className="pm-card-acoes pm-ref-acoes">
                      {botaoEditar(method, ' pm-ref-delete')}
                      {podeExcluir && (
                        <button
                          className="pm-btn-delete pm-ref-delete"
                          onClick={e => { e.stopPropagation(); handleDelete(method); }}
                          title={`Excluir ${method.name}`}
                          aria-label={`Excluir ${method.name}`}
                        >
                          ✕
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </>
      )}
      {/*
        Os pontos ficam por último, embaixo dos métodos sem uso (pedido dela,
        09/10): os cartões e o gasto do mês são o que se vem ver aqui, e os
        pontos são consulta de vez em quando.
      */}
      {!showForm && !loading && <SeusPontos versao={versaoDosPontos} />}
    </div>
  );
};

export default PaymentMethods;
